import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ApiService } from '@app/@shared/services/api/api.service';
import { catchError, map, Observable, of, share, Subject } from 'rxjs';
import { Credentials, CredentialsService } from './credentials.service';
import { SignUpConfig } from './models/signup-config';
import { SingupDetails } from './models/signup-details.model';
import { SignupResponse } from './models/signup-response.model';

const routes = {
  signUpConfig: () => '/org/profile/signup-configs',
  registrationOTP: () => '/org/profile/registration-otp',
  verifyRegistrationOTP: () => '/org/profile/verify-registration-otp',
  checkEmailAvailability: () =>
    '/org/profile/signup-validation-checks/email?value=',
  checkCompanyNameAvailability: () =>
    '/org/profile/signup-validation-checks/company-name?value=',
  checkCompanyDomainNameAvailability: () =>
    '/org/profile/signup-validation-checks/domain-prefix?value=',
  signup: () => '/org/profile/signup',
  resendVerificationEmail: () => '/org/profile/resend-email-verification',
  login: () => `/org/profile/login`,
  refreshToken: () => `/org/profile/token`,
  otpPhones: () => `/org/profile/otp-phones`,
  sendOtp: () => `/org/profile/send-otp`,
  verifyOtp: () => `/org/profile/verify-otp`,
  resetPassword: () => `/org/profile/reset-password`,
};

export interface LoginContext {
  username: string;
  password: string;
  remember?: boolean;
}
@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private _token_refreshed = false;
  private refreshTokenRequest: Observable<any> | undefined;
  getOtpedPhoneNumber = new Subject<string>();
  constructor(
    private router: Router,
    private httpClient: HttpClient,
    private apiSrvc: ApiService,
    private credentialsService: CredentialsService
  ) {}

  // better to get the supported countries list from an API
  getSupportedCountries(): string[] {
    return ['AE', 'SA', 'ARE', 'SAU'];
  }

  getSignupConfig(): Observable<any> {
    return this.httpClient
      .get<SignUpConfig>(routes.signUpConfig(), {
        headers: new HttpHeaders({
          'Content-Type': 'application/x-www-form-urlencoded',
        }),
      })
      .pipe(catchError((error) => this.apiSrvc.handleError(error)));
  }

  getRegistrationOTP(phoneNumber: string = '', email: string | null = null): Observable<any> {

    const payload: any = {phoneNumber};
    if (email) {
      payload.email = email;
    }

    return this.httpClient
      .post(routes.registrationOTP(), payload)
      .pipe(catchError((error) => this.apiSrvc.handleError(error)));
  }

  verifyRegistrationOTP(
    phoneNumber: string = '',
    otp: string
  ): Observable<any> {
    return this.httpClient
      .post(routes.verifyRegistrationOTP(), { phoneNumber, otp })
      .pipe(catchError((error) => this.apiSrvc.handleError(error)));
  }

  checkEmailAvailability(email: string): Observable<any> {
    return this.httpClient
      .get(routes.checkEmailAvailability() + encodeURIComponent(email))
      .pipe(catchError((error) => this.apiSrvc.handleError(error)));
  }

  checkCompanyNameAvailability(companyName: string): Observable<any> {
    return this.httpClient
      .get(routes.checkCompanyNameAvailability() + companyName)
      .pipe(catchError((error) => this.apiSrvc.handleError(error)));
  }

  checkCompanyDomainNameAvailability(domainName: string): Observable<any> {
    return this.httpClient
      .get(routes.checkCompanyDomainNameAvailability() + domainName)
      .pipe(catchError((error) => this.apiSrvc.handleError(error)));
  }

  completeSignUpProcess(
    signupDetails: SingupDetails,
    token?: any
  ): Observable<any> {
    const data = new FormData();

    data.append('businessTypeId', signupDetails.businessTypeId + '');
    data.append('environmentTypeId', signupDetails.environmentTypeId + '');
    data.append('planId', signupDetails.planId + '');
    data.append('billingPeriod', signupDetails.billingPeriod + '');
    data.append('companyInfo.name', signupDetails.companyInfo.name);
    data.append('companyInfo.country', signupDetails.companyInfo.country);
    data.append('companyInfo.logo', signupDetails.companyInfo.logo);
    data.append(
      'companyInfo.domainPrefix',
      signupDetails.companyInfo.domainPrefix
    );
    data.append('personalInfo.firstName', signupDetails.personalInfo.firstName);
    data.append('personalInfo.lastName', signupDetails.personalInfo.lastName);
    data.append('personalInfo.email', signupDetails.personalInfo.email);
    data.append(
      'personalInfo.phoneNumber',
      signupDetails.personalInfo.phoneNumber
    );
    data.append('password', signupDetails.personalInfo.password);
    if (signupDetails.billingInfo) {
      data.append('cardToken', signupDetails.billingInfo.cardToken || '');
      data.append('billingAddress.address', signupDetails.billingInfo.address || '');
      data.append('billingAddress.street', signupDetails.billingInfo.street || '');
      data.append(
        'billingAddress.district',
        signupDetails.billingInfo.district || ''
      );
      data.append('billingAddress.city', signupDetails.billingInfo.city || '');
      data.append('billingAddress.zipCode', signupDetails.billingInfo.zipCode || '');
      data.append(
        'billingAddress.countryCode',
        signupDetails.billingInfo.countryCode || ''
      );
    }

    const regToken = token
      ? {
          headers: new HttpHeaders({
            Authorization: `Bearer ${token}`,
          }),
        }
      : {};
    return this.httpClient
      .post(routes.signup(), data, regToken)
      .pipe(catchError((error) => this.apiSrvc.handleError(error)));
  }

  resendVerificationEmail() {
    return this.httpClient
      .post(
        routes.resendVerificationEmail(),
        {},
        {
          headers: new HttpHeaders({
            Authorization: `Bearer ${this.credentialsService.credentials?.token}`,
          }),
        }
      )
      .pipe(catchError((error) => this.apiSrvc.handleError(error)));
  }

  /**
   * Authenticates the user.
   * @param context The login parameters.
   * @return The user credentials.
   */
  login(context: LoginContext): Observable<any> {
    const body = new URLSearchParams();
    body.set('username', context.username);
    body.set('password', context.password);

    return this.httpClient
      .post(routes.login(), body.toString(), {
        headers: new HttpHeaders({
          'Content-Type': 'application/x-www-form-urlencoded',
        }),
      })
      .pipe(catchError((error) => this.apiSrvc.handleError(error)));
  }

  getUserAllPhoneNumbers(otpToken: string): Observable<any> {
    return this.httpClient
      .get(routes.otpPhones(), {
        headers: new HttpHeaders({
          Authorization: `Bearer ${otpToken}`,
        }),
      })
      .pipe(catchError((error) => this.apiSrvc.handleError(error)));
  }

  sendOtp(otpToken: string, phone: string) {
    return this.httpClient
      .post(
        routes.sendOtp(),
        {
          phoneNumber: phone,
        },
        {
          headers: new HttpHeaders({
            Authorization: `Bearer ${otpToken}`,
          }),
        }
      )
      .pipe(catchError((error) => this.apiSrvc.handleError(error)));
  }

  verifyOtp(
    otpToken: string,
    code: string,
    phoneNumber: string
  ): Observable<any> {
    return this.httpClient
      .post(
        routes.verifyOtp(),
        {
          phoneNumber,
          otp: code,
        },
        {
          headers: new HttpHeaders({
            Authorization: `Bearer ${otpToken}`,
          }),
        }
      )
      .pipe(catchError((error) => this.apiSrvc.handleError(error)));
  }  /**
   * Checks if the token is about to expire or has already expired
   * We consider a token about to expire if it has less than 5 minutes remaining
   * @returns boolean True if token is about to expire or has expired
   */
  private isTokenExpiredOrAboutToExpire(): boolean {
    const credentials = this.credentialsService?.credentials;
    if (!credentials || !credentials.token) {
      // If we don't have a token, assume token needs refresh
      return true;
    }

    try {
      // If we have an expiresIn value, it represents the lifespan in seconds from login time
      // We need to calculate when the token was created and add the expiresIn to get expiration time
      if (credentials.expiresIn) {
        // Get the current time in seconds
        const currentTime = Math.floor(Date.now() / 1000);
        
        // Get token creation time from token (if it's a JWT token)
        let tokenCreationTime = this.getTokenIssuedAt(credentials.token);
        
        // If we couldn't get creation time from token, use a fallback approach
        if (!tokenCreationTime) {
          // We don't know when the token was created, so we have to be conservative
          // and refresh if we're less than 5 minutes from the potential expiration
          return true;
        }
        
        // Calculate when the token expires based on the token creation time plus its lifespan
        const expirationTime = tokenCreationTime + parseInt(credentials.expiresIn, 10);
        
        // Check if token is about to expire (less than 5 minutes remaining)
        const fiveMinutesInSeconds = 5 * 60;
        return currentTime + fiveMinutesInSeconds >= expirationTime;
      }
      
      // If we don't have expiresIn, try to extract expiration directly from the token
      const tokenExpiration = this.getTokenExpiration(credentials.token);
      if (tokenExpiration) {
        // Check if token is about to expire (less than 5 minutes remaining)
        const fiveMinutesInSeconds = 5 * 60;
        const currentTime = Math.floor(Date.now() / 1000);
        return currentTime + fiveMinutesInSeconds >= tokenExpiration;
      }
      
      // If we couldn't determine expiration time, assume token needs refresh
      return true;
    } catch (error) {
      console.error('Error checking token expiration:', error);
      return true; // If there's an error, assume token needs refresh
    }
  }
  
  /**
   * Extracts the expiration time from a JWT token
   * @param token The JWT token
   * @returns number The expiration time in seconds or null if it couldn't be extracted
   */
  private getTokenExpiration(token: string): number | null {
    try {
      // JWT tokens consist of three parts: header.payload.signature
      const parts = token.split('.');
      if (parts.length !== 3) {
        return null; // Not a valid JWT token
      }
      
      // Decode the payload
      const payload = JSON.parse(atob(parts[1]));
      
      // Check for the expiration claim
      if (payload && payload.exp) {
        return payload.exp;
      }
      
      return null;
    } catch (error) {
      console.error('Error decoding token:', error);
      return null;
    }
  }
  
  /**
   * Extracts the issued at time from a JWT token
   * @param token The JWT token
   * @returns number The issued at time in seconds or null if it couldn't be extracted
   */
  private getTokenIssuedAt(token: string): number | null {
    try {
      // JWT tokens consist of three parts: header.payload.signature
      const parts = token.split('.');
      if (parts.length !== 3) {
        return null; // Not a valid JWT token
      }
      
      // Decode the payload
      const payload = JSON.parse(atob(parts[1]));
      
      // Check for the issued at claim
      if (payload && payload.iat) {
        return payload.iat;
      }
      
      return null;
    } catch (error) {
      console.error('Error decoding token:', error);
      return null;
    }
  }

  refreshTokenAsync() {
    if (this._token_refreshed) {
      return of(true);
    }
    
    // Only refresh if the token is about to expire or has expired
    if (!this.isTokenExpiredOrAboutToExpire()) {
      return of(true);
    }
  
    if (!this.refreshTokenRequest) {
      const body = {
        refreshToken: this.credentialsService?.credentials?.refreshToken,
      };
      
      this.refreshTokenRequest = this.httpClient
        .post(routes.refreshToken(), body)
        .pipe(
          map(res => {
            this.updateRefreshedToken(res);
            this._token_refreshed = true;
            return true;
          }),
          catchError(err => {
            this.logout();
            return of(false);
          }),
          share()
        );
    }
  
    return this.refreshTokenRequest;
  }  private updateRefreshedToken(res: any) {
    const data: Credentials = {
      companyId: this.credentialsService?.credentials?.companyId!,
      username: this.credentialsService?.credentials?.username!,
      token: res.accessToken,
      refreshToken: res.refreshToken,
      expiresIn: res.expiresIn || this.getTokenLifespan(res.accessToken),
      companyName: this.credentialsService?.credentials?.companyName!,
      phoneNumber: this.credentialsService?.credentials?.phoneNumber!,
      email: this.credentialsService?.credentials?.email!,
    };
    this.credentialsService.setCredentials(data);
    this.refreshTokenRequest = undefined; // Clear the request so it can be recreated next time
  }

  /**
   * Calculates the token lifespan if not directly provided by the server
   * @param token The JWT token
   * @returns The token lifespan in seconds
   */
  private getTokenLifespan(token: string): string {
    try {
      // Try to get the token expiration and issued at times
      const expiration = this.getTokenExpiration(token);
      const issuedAt = this.getTokenIssuedAt(token);
      
      // If we have both, calculate the lifespan
      if (expiration && issuedAt) {
        const lifespan = expiration - issuedAt;
        return lifespan.toString();
      }
      
      // If we just have the expiration, estimate the lifespan
      // by assuming the token was just issued
      if (expiration) {
        const currentTime = Math.floor(Date.now() / 1000);
        const estimatedLifespan = expiration - currentTime;
        
        // Return a conservative estimate to ensure we refresh before expiration
        return Math.max(estimatedLifespan, 3600).toString(); // At least 1 hour
      }
      
      // Default to 1 hour if we can't determine
      return "3600";
    } catch (error) {
      console.error('Error calculating token lifespan:', error);
      return "3600"; // Default to 1 hour
    }
  }

  resetPassword(body: { emailAddress: string }) {
    return this.httpClient
      .post<{ emailAddress: string }>(routes.resetPassword(), body)
      .pipe(catchError((error) => this.apiSrvc.handleError(error)));
  }
  /**
   * Logs out the user and clear credentials.
   * @return True if the user was logged out successfully.
   */
  logout() {
    this.credentialsService.setCredentials();
    this._token_refreshed = false;
    this.refreshTokenRequest = undefined;
    this.router.navigateByUrl('/auth');
  }

  /**
   * Reset the token_refreshed flag when navigating between pages
   * This ensures that the token will be checked for expiration on each page load
   */
  resetTokenRefreshedFlag() {
    this._token_refreshed = false;
    this.refreshTokenRequest = undefined;
  }
}
